import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useThemeStore, useAuthStore } from '@/stores';
import { useQuery } from '@tanstack/react-query';
import { usersService } from '@/services';
import { usePermission } from '@/hooks/usePermission';
import AccessDenied from '@/components/common/AccessDenied';
import {
  ShieldCheck,
  ShieldAlert,
  Users,
  UserPlus,
  Key,
  Lock,
  Unlock,
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  ArrowRight,
  Edit,
  Trash2,
  RefreshCw,
  Check,
  AlertTriangle,
  FileText,
  Sliders,
  Sparkles,
  ChevronDown,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Clock,
  Shield,
  Eye,
  EyeOff,
  Copy
} from 'lucide-react';

export interface SystemUser {
  id: string;
  username: string;
  fullName: string;
  email: string;
  phone: string;
  roleId: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED' | 'INACTIVE' | 'active' | 'suspended' | string;
  lastLogin: string;
  partnerId: string;
  createdAt: string;
}

export const isUserActive = (status?: string): boolean => (status || '').toUpperCase() === 'ACTIVE';

export interface SystemRole {
  id: string;
  nameAr: string;
  nameEn: string;
  description: string;
  badgeColor: string;
  isSystem: boolean;
  permissions: string[];
}

export interface PermissionItem {
  key: string;
  moduleAr: string;
  moduleEn: string;
  labelAr: string;
  labelEn: string;
  description: string;
}

const ALL_PERMISSIONS: PermissionItem[] = [
  // Tickets Module
  { key: 'tickets.read', moduleAr: 'تذاكر الدعم', moduleEn: 'Support Tickets', labelAr: 'استعراض التذاكر', labelEn: 'View Tickets', description: 'الاطلاع على تذاكر الدعم الفني الخاصة بالمشتركين' },
  { key: 'tickets.create', moduleAr: 'تذاكر الدعم', moduleEn: 'Support Tickets', labelAr: 'إضافة تذكرة جديدة', labelEn: 'Create Ticket', description: 'فتح تذكرة دعم فني جديدة' },
  { key: 'tickets.reply', moduleAr: 'تذاكر الدعم', moduleEn: 'Support Tickets', labelAr: 'إضافة تعليق / رد', labelEn: 'Reply to Ticket', description: 'الرد على الاستفسارات وإضافة ملاحظات التذكرة' },
  { key: 'tickets.manage', moduleAr: 'تذاكر الدعم', moduleEn: 'Support Tickets', labelAr: 'إدارة وإغلاق التذاكر', labelEn: 'Manage & Close Tickets', description: 'تغيير حالة التذكرة وإعادة تعيين المسؤول' },

  // Customers & Subscriptions
  { key: 'customers.read', moduleAr: 'المشتركين', moduleEn: 'Customers', labelAr: 'عرض المشتركين', labelEn: 'View Customers', description: 'استعراض قائمة بيانات المشتركين' },
  { key: 'customers.create', moduleAr: 'المشتركين', moduleEn: 'Customers', labelAr: 'إضافة مشترك', labelEn: 'Add Customer', description: 'تسجيل حساب جديد للمشترك' },
  { key: 'customers.update', moduleAr: 'المشتركين', moduleEn: 'Customers', labelAr: 'تعديل بيانات المشترك', labelEn: 'Update Customer', description: 'تحديث المعلومات الشخصية والاشتراكات' },
  { key: 'subscriptions.read', moduleAr: 'الاشتراكات', moduleEn: 'Subscriptions', labelAr: 'عرض الاشتراكات', labelEn: 'View Subscriptions', description: 'استعراض باقات وحالات الاشتراكات' },
  { key: 'subscriptions.manage', moduleAr: 'الاشتراكات', moduleEn: 'Subscriptions', labelAr: 'تجديد وتعديل الباقات', labelEn: 'Manage Subscriptions', description: 'تجديد الاشتراكات وتغيير الباقة' },

  // Wallet & Finance
  { key: 'wallet.read', moduleAr: 'المحفظة والمالية', moduleEn: 'Wallet & Finance', labelAr: 'عرض الرصيد والمعاملات', labelEn: 'View Wallet', description: 'الاطلاع على كشف حساب المحفظة' },
  { key: 'wallet.transfer', moduleAr: 'المحفظة والمالية', moduleEn: 'Wallet & Finance', labelAr: 'تحويل الرصيد والدفع', labelEn: 'Transfer & Pay', description: 'إجراء تحويلات وإضافة رصيد للمشتركين' },
  { key: 'debts.manage', moduleAr: 'المحفظة والمالية', moduleEn: 'Wallet & Finance', labelAr: 'إدارة الديون والتسويات', labelEn: 'Manage Debts', description: 'تسوية الديون وتسجيل المبالغ المستحقة' },

  // Tasks & Requests
  { key: 'tasks.read', moduleAr: 'المهام والطلبات', moduleEn: 'Tasks & Requests', labelAr: 'عرض المهام الميدانية', labelEn: 'View Tasks', description: 'استعراض قائمة المهام' },
  { key: 'tasks.manage', moduleAr: 'المهام والطلبات', moduleEn: 'Tasks & Requests', labelAr: 'تعيين وتحديث المهام', labelEn: 'Manage Tasks', description: 'إسناد المهام للفنيين وتغيير حالتها' },
  { key: 'requests.read', moduleAr: 'المهام والطلبات', moduleEn: 'Tasks & Requests', labelAr: 'عرض الطلبات', labelEn: 'View Requests', description: 'متابعة طلبات التفعيل والتوصيل' },
  { key: 'requests.manage', moduleAr: 'المهام والطلبات', moduleEn: 'Tasks & Requests', labelAr: 'الموافقة/معالجة الطلبات', labelEn: 'Manage Requests', description: 'اتخاذ قرار بشأن طلبات الخدمة' },

  // System & Settings
  { key: 'settings.read', moduleAr: 'إعدادات النظام', moduleEn: 'System Settings', labelAr: 'عرض الإعدادات العامة', labelEn: 'View Settings', description: 'الاطلاع على إعدادات الحساب والنظام' },
  { key: 'settings.manage', moduleAr: 'إعدادات النظام', moduleEn: 'System Settings', labelAr: 'تعديل إعدادات النظام', labelEn: 'Manage Settings', description: 'تغيير خيارات الربط والتنبيهات' },
  { key: 'users.manage', moduleAr: 'إعدادات النظام', moduleEn: 'System Settings', labelAr: 'إدارة المستخدمين والصلاحيات', labelEn: 'Manage Users & Permissions', description: 'إضافة المستخدمين وتعديل الأدوار والصلاحيات' },
];

const DEFAULT_ROLES: SystemRole[] = [
  {
    id: 'super_admin',
    nameAr: 'مدير نظام فاخر (Super Admin)',
    nameEn: 'Super Admin',
    description: 'يمتلك صلاحيات كاملة ومطلقة لكافة أجزاء ووظائف النظام',
    badgeColor: '#ec4899',
    isSystem: true,
    permissions: ALL_PERMISSIONS.map(p => p.key),
  },
  {
    id: 'contractor_admin',
    nameAr: 'مسؤول المقاول (Contractor Admin)',
    nameEn: 'Contractor Admin',
    description: 'إدارة العمليات اليومية للمقاول بما فيها المالي والدعم والمشتركين',
    badgeColor: '#3b82f6',
    isSystem: true,
    permissions: ALL_PERMISSIONS.map(p => p.key),
  },
  {
    id: 'support_agent',
    nameAr: 'موظف دعم فني (Support Agent)',
    nameEn: 'Support Agent',
    description: 'متابعة وتلبية تذاكر الدعم الفني واستعراض حسابات المشتركين',
    badgeColor: '#10b981',
    isSystem: false,
    permissions: [
      'tickets.read', 'tickets.create', 'tickets.reply',
      'customers.read', 'requests.read'
    ],
  },
  {
    id: 'field_tech',
    nameAr: 'فني ميداني (Field Technician)',
    nameEn: 'Field Technician',
    description: 'تنفيذ المهام الميدانية وربط الحسابات وتحديث حالات الطلبات',
    badgeColor: '#f59e0b',
    isSystem: false,
    permissions: [
      'tickets.read', 'tickets.reply',
      'customers.read', 'tasks.read', 'tasks.manage'
    ],
  },
  {
    id: 'viewer',
    nameAr: 'مراقب (Read Only)',
    nameEn: 'Read Only Viewer',
    description: 'صلاحيات قراءة فقط دون إمكانية التعديل أو إضافة بيانات',
    badgeColor: '#6b7280',
    isSystem: false,
    permissions: [
      'tickets.read', 'customers.read', 'subscriptions.read',
      'tasks.read', 'requests.read', 'settings.read'
    ],
  },
];

const ROLE_METADATA: Record<string, { nameAr: string; nameEn: string; badgeColor: string }> = {
  super_admin: { nameAr: 'مدير نظام فاخر (Super Admin)', nameEn: 'Super Admin', badgeColor: '#ec4899' },
  contractor_admin: { nameAr: 'مسؤول المقاول (Contractor Admin)', nameEn: 'Contractor Admin', badgeColor: '#3b82f6' },
  support_agent: { nameAr: 'موظف دعم فني (Support Agent)', nameEn: 'Support Agent', badgeColor: '#10b981' },
  field_tech: { nameAr: 'فني ميداني (Field Technician)', nameEn: 'Field Technician', badgeColor: '#f59e0b' },
  viewer: { nameAr: 'مراقب (Read Only)', nameEn: 'Read Only Viewer', badgeColor: '#6b7280' },
};

export const UsersPermissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const { user: currentUser } = useAuthStore();
  const { hasPermission } = usePermission();
  const canManageUsers = hasPermission('users.manage');
  const isLight = theme === 'light';

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch Users from DB
  const { data: dbUsers, isLoading: usersLoading, isError: usersError, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await usersService.list();
      return response.data || [];
    },
    enabled: canManageUsers,
  });

  // Fetch Roles from DB
  const { data: dbRoles, isLoading: rolesLoading, isError: rolesError, refetch: refetchRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await usersService.listRoles();
      return response.data || [];
    },
    enabled: canManageUsers,
  });

  if (!canManageUsers) {
    return (
      <AccessDenied
        requiredPermission="users.manage"
        title="غير مصرح — إدارة المستخدمين والصلاحيات"
        message="لا تملك صلاحية إدارة المستخدمين وتعديل الصلاحيات (users.manage). يرجى مراجعة مدير النظام."
      />
    );
  }

  const [users, setUsers] = useState<SystemUser[]>([]);
  const [roles, setRoles] = useState<SystemRole[]>([]);

  useEffect(() => {
    if (dbUsers && Array.isArray(dbUsers)) {
      const mapped = dbUsers.map((u) => ({
        id: u.id,
        username: u.username,
        fullName: u.fullName || u.username,
        email: u.email || `${u.username}@ftthnexus.iq`,
        phone: u.phone || '07700000000',
        roleId: u.roleId || 'contractor_admin',
        status: (u.status || 'ACTIVE').toUpperCase(),
        lastLogin: u.lastLogin || 'الآن',
        partnerId: 'PARTNER-5922',
        createdAt: u.createdAt || new Date().toISOString().split('T')[0],
      }));
      setUsers(mapped);
    }
  }, [dbUsers]);

  useEffect(() => {
    if (dbRoles && Array.isArray(dbRoles) && dbRoles.length > 0) {
      const mapped = dbRoles.map((r) => {
        const meta = ROLE_METADATA[r.name] || { nameAr: r.name, nameEn: r.name, badgeColor: '#64748b' };
        return {
          id: r.name,
          nameAr: meta.nameAr || r.name,
          nameEn: meta.nameEn || r.name,
          description: r.description || '',
          badgeColor: meta.badgeColor || '#3b82f6',
          isSystem: r.isSystem ?? false,
          permissions: (r.permissions || []).map((p: any) => typeof p === 'string' ? p : p.name || p),
        };
      });
      setRoles(mapped);
    }
  }, [dbRoles]);

  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'matrix' | 'test'>('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Modals
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [userFormData, setUserFormData] = useState({
    username: '',
    fullName: '',
    email: '',
    phone: '',
    roleId: 'support_agent',
    status: 'ACTIVE' as 'ACTIVE' | 'SUSPENDED',
  });

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<SystemRole | null>(null);
  const [selectedRoleForPermissions, setSelectedRoleForPermissions] = useState<string>('contractor_admin');

  // Cryptographically secure password generator
  const generateSecurePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*';
    let result = '';
    const randomValues = new Uint32Array(14);
    window.crypto.getRandomValues(randomValues);
    for (let i = 0; i < 14; i++) {
      result += chars[randomValues[i] % chars.length];
    }
    return result;
  };
  const generateStrongPassword = generateSecurePassword;

  const handleGeneratePassword = () => {
    const strong = generateSecurePassword();
    setPassword(strong);
    setConfirmPassword(strong);
    setShowPassword(true);
    setShowConfirmPassword(true);
    setPasswordError(null);
  };

  // Toast Notification State
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const displayRoles = roles.length > 0 ? roles : DEFAULT_ROLES;
  const assignableRoles = displayRoles.filter((r) => r.id !== 'super_admin');

  // Filtered Users
  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.phone.includes(searchQuery);
    const matchesRole = roleFilter === 'all' || u.roleId === roleFilter;
    const matchesStatus = statusFilter === 'all' || (u.status || '').toUpperCase() === statusFilter.toUpperCase();
    return matchesSearch && matchesRole && matchesStatus;
  });

  // User Actions
  const handleOpenAddUser = () => {
    setEditingUser(null);
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPasswordError(null);
    setUserFormData({
      username: '',
      fullName: '',
      email: '',
      phone: '',
      roleId: 'support_agent',
      status: 'ACTIVE',
    });
    setShowUserModal(true);
  };

  const handleOpenEditUser = (userToEdit: SystemUser) => {
    setEditingUser(userToEdit);
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setPasswordError(null);
    setUserFormData({
      username: userToEdit.username,
      fullName: userToEdit.fullName,
      email: userToEdit.email,
      phone: userToEdit.phone,
      roleId: userToEdit.roleId,
      status: ((userToEdit.status || '').toUpperCase() === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE') as 'ACTIVE' | 'SUSPENDED',
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    const trimmedUsername = userFormData.username.trim();
    const trimmedFullName = userFormData.fullName.trim();

    if (!trimmedUsername || !trimmedFullName) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    if (!editingUser) {
      // Add User mode
      if (!password || !confirmPassword) {
        setPasswordError('يرجى إدخال كلمة المرور وتأكيدها');
        return;
      }
      if (password.length < 8) {
        setPasswordError('كلمة المرور يجب أن تتكون من 8 أحرف على الأقل');
        return;
      }
      if (password !== confirmPassword) {
        setPasswordError('كلمتا المرور غير متطابقتين');
        return;
      }
    } else {
      // Edit User mode - if password provided, validate
      if (password) {
        if (password.length < 8) {
          setPasswordError('كلمة المرور يجب أن تتكون من 8 أحرف على الأقل');
          return;
        }
        if (password !== confirmPassword) {
          setPasswordError('كلمتا المرور غير متطابقتين');
          return;
        }
      }
    }

    try {
      if (editingUser) {
        const updatePayload: any = {
          username: trimmedUsername,
          fullName: trimmedFullName,
          email: userFormData.email,
          phone: userFormData.phone,
          roleId: userFormData.roleId,
          status: userFormData.status,
        };
        if (password) {
          updatePayload.password = password;
        }
        await usersService.update(editingUser.id, updatePayload);
        showToast(`تم تحديث بيانات المستخدم ${trimmedFullName} بنجاح`);
      } else {
        await usersService.create({
          username: trimmedUsername,
          fullName: trimmedFullName,
          email: userFormData.email,
          phone: userFormData.phone,
          roleId: userFormData.roleId,
          status: userFormData.status,
          password: password,
        });
        showToast(`تم إضافة المستخدم الجديد ${trimmedFullName} بنجاح`);
      }
      refetchUsers();
      setShowUserModal(false);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'حدث خطأ أثناء حفظ المستخدم';
      alert(errorMsg);
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    const userToToggle = users.find((u) => u.id === userId);
    if (!userToToggle) return;

    const newStatus = isUserActive(userToToggle.status) ? 'SUSPENDED' : 'ACTIVE';
    try {
      await usersService.update(userId, { status: newStatus });
      showToast(`تم ${newStatus === 'ACTIVE' ? 'تنشيط' : 'تجميد'} حساب ${userToToggle.fullName}`);
      refetchUsers();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'حدث خطأ أثناء تعديل حالة الحساب');
    }
  };

  const handleDeleteUser = async (userId: string, name: string) => {
    if (confirm(`هل أنت تأكد من رغبتك في حذف المستخدم (${name})؟`)) {
      try {
        await usersService.delete(userId);
        showToast(`تم حذف المستخدم ${name} بنجاح`);
        refetchUsers();
      } catch (err: any) {
        alert(err?.response?.data?.message || err?.message || 'حدث خطأ أثناء حذف المستخدم');
      }
    }
  };

  // Permission Toggle for Role
  const handleTogglePermissionForRole = async (roleId: string, permissionKey: string) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;

    const exists = role.permissions.includes(permissionKey);
    const updatedPermissions = exists
      ? role.permissions.filter((p) => p !== permissionKey)
      : [...role.permissions, permissionKey];

    try {
      await usersService.updateRolePermissions(roleId, updatedPermissions);
      showToast('تم تحديث صلاحية الدور مباشرة ورصد التغييرات في النظام');
      refetchRoles();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'حدث خطأ أثناء تعديل الصلاحيات');
    }
  };

  // Select all permissions for role
  const handleGrantAllPermissions = async (roleId: string) => {
    try {
      await usersService.updateRolePermissions(roleId, ALL_PERMISSIONS.map((p) => p.key));
      showToast('تم تفعيل جميع الصلاحيات لهذا الدور');
      refetchRoles();
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'حدث خطأ أثناء تفعيل الصلاحيات');
    }
  };

  // Reset to Defaults
  const handleResetToDefaults = () => {
    alert('إعادة الضبط الافتراضي غير مدعومة في وضع قواعد البيانات الخلفية المباشر.');
  };

  const activeRoleObj = (roles.length > 0 ? roles : DEFAULT_ROLES).find((r) => r.id === selectedRoleForPermissions) ||
                        roles[0] ||
                        DEFAULT_ROLES[0];

  // Group Permissions by Module
  const permissionsByModule = ALL_PERMISSIONS.reduce((acc, perm) => {
    if (!acc[perm.moduleAr]) {
      acc[perm.moduleAr] = [];
    }
    acc[perm.moduleAr].push(perm);
    return acc;
  }, {} as Record<string, PermissionItem[]>);

  if (usersLoading || rolesLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', direction: 'rtl', backgroundColor: isLight ? '#f8fafc' : '#0f172a', color: isLight ? '#1e293b' : '#f8fafc' }}>
        <div style={{ border: '4px solid #e2e8f0', borderTop: '4px solid #3b82f6', borderRadius: '50%', width: '45px', height: '45px', animation: 'spin 1s linear infinite' }}></div>
        <span style={{ marginRight: '16px', fontWeight: 600, fontSize: '16px' }}>جاري تحميل المستخدمين والصلاحيات...</span>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (usersError || rolesError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          direction: 'rtl',
          backgroundColor: isLight ? '#f8fafc' : '#0f172a',
          color: isLight ? '#1e293b' : '#f8fafc',
          padding: '24px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: 'rgba(239, 68, 68, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
            color: '#ef4444',
          }}
        >
          <AlertTriangle size={32} />
        </div>
        <h2 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>
          فشل في تحميل المستخدمين والصلاحيات
        </h2>
        <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '440px', marginBottom: '20px', lineHeight: 1.5 }}>
          تعذر الاتصال بخادم النظام المحلي (Local Worker D1). يرجى التأكد من تشغيل الخادم والمحاولة مرة أخرى.
        </p>
        <button
          onClick={() => {
            refetchUsers();
            refetchRoles();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 22px',
            fontSize: '14px',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
          }}
        >
          <RefreshCw size={16} />
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '24px',
        direction: 'rtl',
        minHeight: '100vh',
        maxWidth: '1280px',
        margin: '0 auto',
        color: isLight ? '#1e293b' : '#f8fafc',
      }}
    >
      {/* Toast Banner */}
      {toastMessage && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: 'linear-gradient(90deg, #10b981, #059669)',
            color: '#fff',
            padding: '12px 24px',
            borderRadius: '10px',
            boxShadow: '0 10px 25px rgba(16,185,129,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
            fontWeight: 700,
            animation: 'fadeInDown 0.3s ease-out',
          }}
        >
          <CheckCircle2 size={18} />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header & Breadcrumb */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#94a3b8', fontSize: '13px' }}>
          <button
            onClick={() => navigate('/settings')}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: 600,
              padding: 0,
            }}
          >
            <span>الإعدادات</span>
          </button>
          <span>/</span>
          <span style={{ color: isLight ? '#64748b' : '#cbd5e1' }}>إدارة المستخدمين والصلاحيات</span>
        </div>

        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center', 
          justifyContent: 'space-between', 
          gap: '16px' 
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center', 
            gap: '12px' 
          }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
                flexShrink: 0
              }}
            >
              <ShieldCheck size={26} />
            </div>
            <div>
              <h1 style={{ 
                fontSize: isMobile ? '18px' : '22px', 
                fontWeight: 800, 
                margin: 0,
                lineHeight: 1.3,
                wordBreak: 'break-word'
              }}>
                إدارة المستخدمين والصلاحيات (Users & Permissions)
              </h1>
              <p style={{ 
                margin: 0, 
                color: isLight ? '#64748b' : '#94a3b8', 
                fontSize: isMobile ? '12px' : '13px', 
                marginTop: '4px',
                lineHeight: 1.4
              }}>
                نظام حماية متكامل لتحديد أدوار المستويات، وإسناد الصلاحيات الدقيقة للمستخدمين والموظفين.
              </p>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px',
            width: isMobile ? '100%' : 'auto',
            flexDirection: isMobile ? 'column-reverse' : 'row',
            marginTop: isMobile ? '6px' : '0'
          }}>
            <button
              onClick={handleResetToDefaults}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
                color: isLight ? '#475569' : '#94a3b8',
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                padding: '10px 14px',
                fontSize: '12.5px',
                fontWeight: 600,
                cursor: 'pointer',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              <RefreshCw size={14} />
              إعادة الضبط الافتراضي
            </button>

            <button
              onClick={handleOpenAddUser}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '11px 18px',
                fontSize: '13.5px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              <UserPlus size={16} />
              إضافة مستخدم جديد
            </button>
          </div>
        </div>
      </div>

      {/* Overview Stat Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: isMobile ? '10px' : '16px',
          marginBottom: '24px',
        }}
      >
        <StatCard
          icon={<Users size={20} color="#3b82f6" />}
          title="إجمالي المستخدمين"
          value={users.length.toString()}
          subtitle={`${users.filter((u) => isUserActive(u.status)).length} نشط`}
          isLight={isLight}
        />
        <StatCard
          icon={<Shield size={20} color="#ec4899" />}
          title="الأدوار المعتمدة"
          value={roles.length.toString()}
          subtitle="أدوار نظامية"
          isLight={isLight}
        />
        <StatCard
          icon={<Key size={20} color="#10b981" />}
          title="صلاحيات التحكم"
          value={ALL_PERMISSIONS.length.toString()}
          subtitle="موزعة على 5 وحدات"
          isLight={isLight}
        />
        <StatCard
          icon={<UserCheck size={20} color="#f59e0b" />}
          title="الحسابات النشطة"
          value={users.filter((u) => isUserActive(u.status)).length.toString()}
          subtitle="جاهزة للعمل"
          isLight={isLight}
        />
      </div>

      {/* Tabs Navigation */}
      <div
        style={{
          display: isMobile ? 'grid' : 'flex',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'none',
          gap: isMobile ? '8px' : '4px',
          borderBottom: isMobile ? 'none' : (isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)'),
          marginBottom: '24px',
          overflowX: isMobile ? 'visible' : 'auto',
          whiteSpace: isMobile ? 'normal' : 'nowrap',
          paddingBottom: isMobile ? '0' : '4px',
        }}
      >

        <TabButton
          active={activeTab === 'users'}
          onClick={() => setActiveTab('users')}
          icon={<Users size={16} />}
          label={`قائمة المستخدمين (${users.length})`}
          isLight={isLight}
        />
        <TabButton
          active={activeTab === 'roles'}
          onClick={() => setActiveTab('roles')}
          icon={<ShieldCheck size={16} />}
          label="إدارة الأدوار والصلاحيات"
          isLight={isLight}
        />
        <TabButton
          active={activeTab === 'matrix'}
          onClick={() => setActiveTab('matrix')}
          icon={<Sliders size={16} />}
          label="مصفوفة الصلاحيات الشاملة"
          isLight={isLight}
        />
        <TabButton
          active={activeTab === 'test'}
          onClick={() => setActiveTab('test')}
          icon={<Sparkles size={16} />}
          label="مختبر المحاكاة والتحقق"
          isLight={isLight}
        />
      </div>


      {/* TAB 1: USERS LIST */}
      {activeTab === 'users' && (
        <div>
          {/* Controls & Search */}
          <div
            style={{
              background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px',
              padding: isMobile ? '12px' : '16px 20px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '14px',
            }}
          >
            {/* Filters */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              flexWrap: 'wrap',
              width: '100%',
              justifyContent: isMobile ? 'space-between' : 'flex-start',
            }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: isMobile ? 1 : 'initial' }}>
                <Filter size={14} color="#94a3b8" />
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>الدور:</span>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
                    background: isLight ? '#ffffff' : '#0d1f3c',
                    color: isLight ? '#0f172a' : '#fff',
                    fontSize: '12.5px',
                    outline: 'none',
                    width: isMobile ? '100%' : 'auto',
                    flex: isMobile ? 1 : 'initial',
                  }}
                >
                  <option value="all">كافة الأدوار</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nameAr}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: isMobile ? 1 : 'initial' }}>
                <span style={{ fontSize: '12px', color: '#94a3b8' }}>الحالة:</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
                    background: isLight ? '#ffffff' : '#0d1f3c',
                    color: isLight ? '#0f172a' : '#fff',
                    fontSize: '12.5px',
                    outline: 'none',
                    width: isMobile ? '100%' : 'auto',
                    flex: isMobile ? 1 : 'initial',
                  }}
                >
                  <option value="all">كافة الحالات</option>
                  <option value="ACTIVE">نشط فقط</option>
                  <option value="SUSPENDED">مجمد فقط</option>
                </select>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {usersLoading && (
            <div style={{
              background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              color: '#94a3b8'
            }}>
              <RefreshCw className="animate-spin" size={32} style={{ margin: '0 auto 12px', display: 'block', color: '#3b82f6' }} />
              جاري تحميل بيانات المستخدمين من الخادم...
            </div>
          )}

          {/* Error State */}
          {!usersLoading && usersError && (
            <div style={{
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: '12px',
              padding: '30px 20px',
              textAlign: 'center',
              color: '#f87171',
              marginBottom: '20px'
            }}>
              <AlertTriangle size={36} style={{ margin: '0 auto 10px', display: 'block', color: '#ef4444' }} />
              <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '6px' }}>فشل في استرجاع بيانات المستخدمين</div>
              <div style={{ fontSize: '12.5px', opacity: 0.85, marginBottom: '14px' }}>
                حدث خطأ أثناء الاتصال بالخادم. لم يتم تحميل أي بيانات وهمية لضمان أمان النظام.
              </div>
              <button
                onClick={() => { (refetchUsers as any)?.(); }}
                style={{
                  padding: '8px 18px',
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '12.5px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <RefreshCw size={14} />
                إعادة المحاولة
              </button>
            </div>
          )}

          {/* Users Table */}
          {!usersLoading && !usersError && (isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredUsers.length === 0 ? (
                <div style={{
                  background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
                  border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  padding: '40px',
                  textAlign: 'center',
                  color: '#94a3b8'
                }}>
                  <UserX size={36} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.5 }} />
                  لا يوجد مستخدمون يطابقون معايير البحث والفلترة.
                </div>
              ) : (
                filteredUsers.map((u) => {
                  const userRole = roles.find((r) => r.id === u.roleId) ||
                                   DEFAULT_ROLES.find((r) => r.id === u.roleId) ||
                                   roles[0] ||
                                   DEFAULT_ROLES[0];
                  const userBadgeColor = userRole?.badgeColor || '#3b82f6';
                  const userNameAr = userRole?.nameAr || u.roleId || 'مستخدم';
                  return (
                    <div
                      key={u.id}
                      style={{
                        background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
                        border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
                      }}
                    >
                      {/* Top Row: User basic info & Status */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div
                            style={{
                              width: '38px',
                              height: '38px',
                              borderRadius: '50%',
                              background: userBadgeColor,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 800,
                              fontSize: '14px',
                            }}
                          >
                            {u.fullName.charAt(0)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '13.5px' }}>
                              {u.fullName}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                              @{u.username}
                            </div>
                          </div>
                        </div>

                        {isUserActive(u.status) ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 8px',
                              borderRadius: '12px',
                              background: 'rgba(16,185,129,0.12)',
                              color: '#10b981',
                              border: '1px solid rgba(16,185,129,0.3)',
                              fontSize: '10.5px',
                              fontWeight: 700,
                            }}
                          >
                            <CheckCircle2 size={11} />
                            نشط
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 8px',
                              borderRadius: '12px',
                              background: 'rgba(239,68,68,0.12)',
                              color: '#f87171',
                              border: '1px solid rgba(239,68,68,0.3)',
                              fontSize: '10.5px',
                              fontWeight: 700,
                            }}
                          >
                            <XCircle size={11} />
                            مجمد
                          </span>
                        )}
                      </div>

                      {/* Info lines */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '10px 8px',
                        borderTop: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.04)',
                        borderBottom: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.04)',
                        padding: '10px 0',
                        fontSize: '12px'
                      }}>
                        <div>
                          <div style={{ color: '#94a3b8', fontSize: '10.5px', marginBottom: '2px' }}>الدور المسند:</div>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 700,
                              background: `${userBadgeColor}18`,
                              color: userBadgeColor,
                            }}
                          >
                            <Shield size={10} />
                            {userNameAr}
                          </span>
                        </div>

                        <div>
                          <div style={{ color: '#94a3b8', fontSize: '10.5px', marginBottom: '2px' }}>معرف المقاول:</div>
                          <span style={{ fontWeight: 600, color: isLight ? '#475569' : '#cbd5e1' }}>{u.partnerId || '—'}</span>
                        </div>

                        <div>
                          <div style={{ color: '#94a3b8', fontSize: '10.5px', marginBottom: '2px' }}>آخر ظهور:</div>
                          <span style={{ color: isLight ? '#475569' : '#cbd5e1', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                            <Clock size={11} color="#94a3b8" />
                            {u.lastLogin}
                          </span>
                        </div>

                        <div>
                          <div style={{ color: '#94a3b8', fontSize: '10.5px', marginBottom: '2px' }}>التواصل والبريد:</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px', color: '#64748b' }}>
                            <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '140px' }} title={u.email}>{u.email}</span>
                            <span>{u.phone}</span>
                          </div>
                        </div>
                      </div>

                      {/* Actions row */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <button
                          onClick={() => handleToggleUserStatus(u.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            border: 'none',
                            background: isUserActive(u.status) ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                            color: isUserActive(u.status) ? '#ef4444' : '#10b981',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '11.5px',
                            fontWeight: 600
                          }}
                        >
                          {isUserActive(u.status) ? (
                            <>
                              <UserX size={13} />
                              <span>تجميد الحساب</span>
                            </>
                          ) : (
                            <>
                              <UserCheck size={13} />
                              <span>تنشيط الحساب</span>
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => handleOpenEditUser(u)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            border: 'none',
                            background: 'rgba(59,130,246,0.1)',
                            color: '#3b82f6',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '11.5px',
                            fontWeight: 600
                          }}
                        >
                          <Edit size={13} />
                          <span>تعديل</span>
                        </button>

                        <button
                          onClick={() => handleDeleteUser(u.id, u.fullName)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            border: 'none',
                            background: 'rgba(239,68,68,0.1)',
                            color: '#f87171',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '11.5px',
                            fontWeight: 600
                          }}
                        >
                          <Trash2 size={13} />
                          <span>حذف</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div
              style={{
                background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px',
                overflowX: 'auto',
                boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
              }}
            >
              <table style={{ minWidth: '100%', width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '13px' }}>
                <thead>
                  <tr
                    style={{
                      background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)',
                      borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
                      color: isLight ? '#475569' : '#94a3b8',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    <th style={{ padding: '14px 18px' }}>المستخدم</th>
                    <th style={{ padding: '14px 18px' }}>التواصل والبريد</th>
                    <th style={{ padding: '14px 18px' }}>الدور المسند</th>
                    <th style={{ padding: '14px 18px' }}>معرف المقاول</th>
                    <th style={{ padding: '14px 18px' }}>آخر ظهور</th>
                    <th style={{ padding: '14px 18px' }}>الحالة</th>
                    <th style={{ padding: '14px 18px', textAlign: 'center' }}>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        <UserX size={36} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.5 }} />
                        لا يوجد مستخدمون يطابقون معايير البحث والفلترة.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => {
                      const userRole = roles.find((r) => r.id === u.roleId) ||
                                       DEFAULT_ROLES.find((r) => r.id === u.roleId) ||
                                       roles[0] ||
                                       DEFAULT_ROLES[0];
                      const userBadgeColor = userRole?.badgeColor || '#3b82f6';
                      const userNameAr = userRole?.nameAr || u.roleId || 'مستخدم';
                      return (
                        <tr
                          key={u.id}
                          style={{
                            borderBottom: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.04)',
                            transition: 'background 0.15s',
                          }}
                        >
                          {/* User info */}
                          <td style={{ padding: '14px 18px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  background: userBadgeColor,
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 800,
                                  fontSize: '14px',
                                  flexShrink: 0,
                                }}
                              >
                                {u.fullName.charAt(0)}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, color: isLight ? '#0f172a' : '#f1f5f9' }}>
                                  {u.fullName}
                                </div>
                                <div style={{ fontSize: '11.5px', color: '#64748b', fontFamily: 'monospace' }}>
                                  @{u.username}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Email & Phone */}
                          <td style={{ padding: '14px 18px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '12px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: isLight ? '#334155' : '#cbd5e1' }}>
                                <Mail size={12} color="#94a3b8" />
                                {u.email}
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#64748b' }}>
                                <Phone size={12} color="#94a3b8" />
                                {u.phone}
                              </span>
                            </div>
                          </td>

                          {/* Role Badge */}
                          <td style={{ padding: '14px 18px' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '11.5px',
                                fontWeight: 700,
                                background: `${userBadgeColor}18`,
                                color: userBadgeColor,
                                border: `1px solid ${userBadgeColor}40`,
                              }}
                            >
                              <Shield size={12} />
                              {userNameAr}
                            </span>
                          </td>

                          {/* Partner ID */}
                          <td style={{ padding: '14px 18px', color: '#64748b', fontSize: '12px', fontWeight: 600 }}>
                            {u.partnerId}
                          </td>

                          {/* Last Login */}
                          <td style={{ padding: '14px 18px', color: '#94a3b8', fontSize: '12px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Clock size={12} />
                              {u.lastLogin}
                            </span>
                          </td>

                          {/* Status Badge */}
                          <td style={{ padding: '14px 18px' }}>
                            {isUserActive(u.status) ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  background: 'rgba(16,185,129,0.12)',
                                  color: '#10b981',
                                  border: '1px solid rgba(16,185,129,0.3)',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                }}
                              >
                                <CheckCircle2 size={12} />
                                نشط
                              </span>
                            ) : (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  background: 'rgba(239,68,68,0.12)',
                                  color: '#f87171',
                                  border: '1px solid rgba(239,68,68,0.3)',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                }}
                              >
                                <XCircle size={12} />
                                مجمد
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                              {/* Toggle Active */}
                              <button
                                onClick={() => handleToggleUserStatus(u.id)}
                                title={isUserActive(u.status) ? 'تجميد الحساب' : 'تنشيط الحساب'}
                                style={{
                                  border: 'none',
                                  background: isUserActive(u.status) ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                                  color: isUserActive(u.status) ? '#ef4444' : '#10b981',
                                  padding: '6px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                }}
                              >
                                {isUserActive(u.status) ? <UserX size={15} /> : <UserCheck size={15} />}
                              </button>

                              {/* Edit User */}
                              <button
                                onClick={() => handleOpenEditUser(u)}
                                title="تعديل المستخدم"
                                style={{
                                  border: 'none',
                                  background: 'rgba(59,130,246,0.1)',
                                  color: '#3b82f6',
                                  padding: '6px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                }}
                              >
                                <Edit size={15} />
                              </button>

                              {/* Delete User */}
                              <button
                                onClick={() => handleDeleteUser(u.id, u.fullName)}
                                title="حذف المستخدم"
                                style={{
                                  border: 'none',
                                  background: 'rgba(239,68,68,0.1)',
                                  color: '#f87171',
                                  padding: '6px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                }}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}


      {/* TAB 2: ROLES & PERMISSIONS MANAGEMENT */}
      {activeTab === 'roles' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px 1fr', gap: '20px' }}>
          {/* Left Panel: List of Roles */}
          <div>
            <div
              style={{
                background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
                border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 800, margin: 0 }}>الأدوار المتاحة</h3>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>{roles.length} أدوار</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {roles.map((r) => {
                  const isSelected = selectedRoleForPermissions === r.id;
                  const usersCount = users.filter((u) => u.roleId === r.id).length;
                  return (
                    <button
                      key={r.id}
                      onClick={() => setSelectedRoleForPermissions(r.id)}
                      style={{
                        textAlign: 'right',
                        padding: '12px 14px',
                        borderRadius: '10px',
                        border: isSelected
                          ? `1.5px solid ${r.badgeColor}`
                          : isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)',
                        background: isSelected
                          ? `${r.badgeColor}15`
                          : isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)',
                        color: isLight ? '#0f172a' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 700, fontSize: '13px', color: isSelected ? r.badgeColor : undefined }}>
                          {r.nameAr}
                        </span>
                        {r.isSystem && (
                          <span style={{ fontSize: '10px', background: 'rgba(100,116,139,0.2)', padding: '2px 6px', borderRadius: '4px', color: '#94a3b8' }}>
                            نظامي
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {r.permissions.length} صلاحيات مفعّلة • {usersCount} مستخدمين
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Panel: Granular Permissions Checklist for Selected Role */}
          <div
            style={{
              background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px',
              padding: isMobile ? '16px' : '24px',
            }}
          >
            {/* Selected Role Header */}
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center', 
              justifyContent: 'space-between', 
              marginBottom: '20px', 
              borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)', 
              paddingBottom: '16px',
              gap: isMobile ? '12px' : '0'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Shield size={20} color={activeRoleObj?.badgeColor || '#3b82f6'} />
                  <h2 style={{ fontSize: '17px', fontWeight: 800, margin: 0 }}>
                    صلاحيات: {activeRoleObj?.nameAr || ''}
                  </h2>
                </div>
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '12.5px', marginTop: '4px', lineHeight: 1.4 }}>
                  {activeRoleObj?.description || ''}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
                <button
                  onClick={() => activeRoleObj && handleGrantAllPermissions(activeRoleObj.id)}
                  style={{
                    background: 'rgba(16,185,129,0.12)',
                    color: '#10b981',
                    border: '1px solid rgba(16,185,129,0.3)',
                    padding: '8px 14px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    width: isMobile ? '100%' : 'auto',
                    textAlign: 'center'
                  }}
                >
                  منح كافة الصلاحيات
                </button>
              </div>
            </div>


            {/* Modules Grid */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {Object.entries(permissionsByModule).map(([moduleName, permItems]) => {
                const grantedCount = permItems.filter((p) => (activeRoleObj?.permissions || []).includes(p.key)).length;
                return (
                  <div
                    key={moduleName}
                    style={{
                      background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.18)',
                      border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)',
                      borderRadius: '10px',
                      padding: '16px',
                    }}
                  >
                    {/* Module Title Bar */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <span style={{ fontWeight: 800, fontSize: '14px', color: '#3b82f6' }}>
                        {moduleName}
                      </span>
                      <span style={{ fontSize: '11.5px', color: '#94a3b8', fontWeight: 600 }}>
                        {grantedCount} من أصل {permItems.length} مفعلة
                      </span>
                    </div>

                    {/* Permissions list */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px' }}>
                      {permItems.map((perm) => {
                        const isGranted = (activeRoleObj?.permissions || []).includes(perm.key);
                        return (
                          <div
                            key={perm.key}
                            onClick={() => activeRoleObj && handleTogglePermissionForRole(activeRoleObj.id, perm.key)}
                            style={{
                              padding: '10px 14px',
                              borderRadius: '8px',
                              background: isGranted
                                ? isLight ? '#eff6ff' : 'rgba(59,130,246,0.12)'
                                : isLight ? '#ffffff' : 'rgba(255,255,255,0.02)',
                              border: isGranted
                                ? '1px solid rgba(59,130,246,0.4)'
                                : isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.06)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '10px',
                              transition: 'all 0.15s',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isGranted}
                              onChange={() => {}}
                              style={{ marginTop: '3px', cursor: 'pointer' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 700, fontSize: '12.5px', color: isGranted ? (isLight ? '#1d4ed8' : '#60a5fa') : undefined }}>
                                  {perm.labelAr}
                                </span>
                                <code style={{ fontSize: '10px', color: '#94a3b8', background: 'rgba(0,0,0,0.15)', padding: '1px 5px', borderRadius: '4px' }}>
                                  {perm.key}
                                </code>
                              </div>
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', lineHeight: 1.4 }}>
                                {perm.description}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: FULL PERMISSIONS MATRIX */}
      {activeTab === 'matrix' && (
        <div
          style={{
            background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
            border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '20px',
          }}
        >
          <div style={{ marginBottom: '16px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>
              مصفوفة المقارنة الشاملة للصلاحيات
            </h2>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '12.5px', marginTop: '4px' }}>
              نظرة عامة لمقارنة صلاحيات الأدوار المختلفة للنظام جنباً إلى جنب.
            </p>
          </div>

          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {ALL_PERMISSIONS.map((perm) => (
                <div 
                  key={perm.key} 
                  style={{
                    padding: '14px',
                    background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '10px',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: '13px', color: isLight ? '#0f172a' : '#e2e8f0', marginBottom: '6px' }}>
                    {perm.labelAr}
                  </div>
                  <div style={{ fontSize: '10.5px', color: '#94a3b8', marginBottom: '12px' }}>
                    {perm.moduleAr} • <code>{perm.key}</code>
                  </div>
                  
                  {/* Roles list wrap */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {roles.map((r) => {
                      const hasPerm = r.permissions.includes(perm.key);
                      return (
                        <span
                          key={r.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background: hasPerm ? 'rgba(16,185,129,0.12)' : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)'),
                            color: hasPerm ? '#10b981' : '#94a3b8',
                            border: `1px solid ${hasPerm ? 'rgba(16,185,129,0.25)' : (isLight ? '#cbd5e1' : 'rgba(255,255,255,0.06)')}`,
                            opacity: hasPerm ? 1 : 0.65,
                          }}
                        >
                          {hasPerm ? <Check size={10} /> : <span style={{ width: '10px', textAlign: 'center' }}>-</span>}
                          <span>{r.nameAr}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '12.5px' }}>
                <thead>
                  <tr
                    style={{
                      background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)',
                      borderBottom: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <th style={{ padding: '12px', minWidth: '220px' }}>الصلاحية / الوحدة</th>
                    {roles.map((r) => (
                      <th key={r.id} style={{ padding: '12px', textAlign: 'center', minWidth: '140px' }}>
                        <span style={{ color: r.badgeColor, fontWeight: 800 }}>{r.nameAr}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ALL_PERMISSIONS.map((perm) => (
                    <tr
                      key={perm.key}
                      style={{
                        borderBottom: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.04)',
                      }}
                    >
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 700, color: isLight ? '#0f172a' : '#e2e8f0' }}>{perm.labelAr}</div>
                        <div style={{ fontSize: '10.5px', color: '#94a3b8' }}>
                          {perm.moduleAr} • <code>{perm.key}</code>
                        </div>
                      </td>

                      {roles.map((r) => {
                        const hasPerm = r.permissions.includes(perm.key);
                        return (
                          <td key={r.id} style={{ padding: '10px 12px', textAlign: 'center' }}>
                            {hasPerm ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  background: 'rgba(16,185,129,0.15)',
                                  color: '#10b981',
                                }}
                              >
                                <Check size={14} />
                              </span>
                            ) : (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  background: 'rgba(239,68,68,0.08)',
                                  color: '#64748b',
                                  opacity: 0.5,
                                }}
                              >
                                -
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {/* TAB 4: LIVE PERMISSION SIMULATION TESTER */}
      {activeTab === 'test' && (
        <div
          style={{
            background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
            border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            padding: '24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <Sparkles size={22} color="#3b82f6" />
            <div>
              <h2 style={{ fontSize: '16.5px', fontWeight: 800, margin: 0 }}>
                مختبر محاكاة فحص الصلاحيات المباشرة
              </h2>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '12.5px', marginTop: '2px' }}>
                اختبر تجربة الوصول والقيود التي يواجهها المستخدمون عند فتح أجزاء النظام المختلفة.
              </p>
            </div>
          </div>

          <div
            style={{
              background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
              border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
              borderRadius: '10px',
              padding: '20px',
              marginBottom: '20px',
            }}
          >
            <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px' }}>اختبر دور المستخدم:</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {roles.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedRoleForPermissions(r.id)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: selectedRoleForPermissions === r.id ? `2px solid ${r.badgeColor}` : '1px solid transparent',
                    background: selectedRoleForPermissions === r.id ? `${r.badgeColor}20` : (isLight ? '#ffffff' : 'rgba(255,255,255,0.05)'),
                    color: selectedRoleForPermissions === r.id ? r.badgeColor : (isLight ? '#334155' : '#cbd5e1'),
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                  }}
                >
                  {r.nameAr}
                </button>
              ))}
            </div>
          </div>

          <h3 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '14px' }}>نتيجة محاكاة وصول الصفحات:</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' }}>
            <SimulationCard
              title="صفحة تذاكر الدعم الفني"
              requiredPermission="tickets.read"
              hasPermission={activeRoleObj.permissions.includes('tickets.read')}
              description="استعراض والرد على تذاكر الدعم"
              isLight={isLight}
            />
            <SimulationCard
              title="إضافة تذكرة جديدة"
              requiredPermission="tickets.create"
              hasPermission={activeRoleObj.permissions.includes('tickets.create')}
              description="فتح تذكرة جديدة في النظام"
              isLight={isLight}
            />
            <SimulationCard
              title="تحويل الرصيد والمحفظة"
              requiredPermission="wallet.transfer"
              hasPermission={activeRoleObj.permissions.includes('wallet.transfer')}
              description="إرسال واستلام الرصيد المالي"
              isLight={isLight}
            />
            <SimulationCard
              title="تجديد وتعديل اشتراك"
              requiredPermission="subscriptions.manage"
              hasPermission={activeRoleObj.permissions.includes('subscriptions.manage')}
              description="تحديث الباقة للمشتركين"
              isLight={isLight}
            />
          </div>
        </div>
      )}

      {/* USER ADD/EDIT MODAL */}
      {showUserModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            style={{
              background: isLight ? '#ffffff' : '#0d1f3c',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '480px',
              padding: '24px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
              direction: 'rtl',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '17px', fontWeight: 800, margin: 0 }}>
                {editingUser ? 'تعديل بيانات المستخدم' : 'إضافة مستخدم جديد'}
              </h3>
              <button
                onClick={() => setShowUserModal(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <XCircle size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveUser} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12.5px', color: '#94a3b8', marginBottom: '4px', fontWeight: 600 }}>
                  اسم المستخدم (Username) *
                </label>
                <input
                  type="text"
                  required
                  value={userFormData.username}
                  onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                  placeholder="مثال: ahmed_tech"
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                    background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                    color: isLight ? '#0f172a' : '#fff',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', color: '#94a3b8', marginBottom: '4px', fontWeight: 600 }}>
                  الاسم الكامل (Full Name) *
                </label>
                <input
                  type="text"
                  required
                  value={userFormData.fullName}
                  onChange={(e) => setUserFormData({ ...userFormData, fullName: e.target.value })}
                  placeholder="مثال: أحمد علي حسن"
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                    background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                    color: isLight ? '#0f172a' : '#fff',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', color: '#94a3b8', marginBottom: '4px', fontWeight: 600 }}>
                    البريد الإلكتروني
                  </label>
                  <input
                    type="email"
                    value={userFormData.email}
                    onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                    placeholder="user@example.com"
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                      background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                      color: isLight ? '#0f172a' : '#fff',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12.5px', color: '#94a3b8', marginBottom: '4px', fontWeight: 600 }}>
                    رقم الهاتف
                  </label>
                  <input
                    type="text"
                    value={userFormData.phone}
                    onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                    placeholder="07700000000"
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      borderRadius: '8px',
                      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                      background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                      color: isLight ? '#0f172a' : '#fff',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* Password Fields */}
              {!editingUser ? (
                <>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontSize: '12.5px', color: '#94a3b8', fontWeight: 600 }}>
                        كلمة المرور المؤقتة *
                      </label>
                      <button
                        type="button"
                        onClick={generateSecurePassword}
                        style={{
                          background: 'rgba(59, 130, 246, 0.12)',
                          border: '1px solid rgba(59, 130, 246, 0.35)',
                          color: '#3b82f6',
                          borderRadius: '6px',
                          padding: '3px 8px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        <Sparkles size={12} />
                        إنشاء كلمة مرور قوية
                      </button>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (passwordError) setPasswordError(null);
                        }}
                        dir="ltr"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        style={{
                          width: '100%',
                          padding: '9px 38px 9px 12px',
                          borderRadius: '8px',
                          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                          background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                          color: isLight ? '#0f172a' : '#fff',
                          fontSize: '13px',
                          outline: 'none',
                          textAlign: 'left',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12.5px', color: '#94a3b8', marginBottom: '4px', fontWeight: 600 }}>
                      تأكيد كلمة المرور *
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (passwordError) setPasswordError(null);
                        }}
                        dir="ltr"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        style={{
                          width: '100%',
                          padding: '9px 38px 9px 12px',
                          borderRadius: '8px',
                          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                          background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                          color: isLight ? '#0f172a' : '#fff',
                          fontSize: '13px',
                          outline: 'none',
                          textAlign: 'left',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title={showConfirmPassword ? 'إخفاء تأكيد كلمة المرور' : 'إظهار تأكيد كلمة المرور'}
                      >
                        {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div style={{ fontSize: '11.5px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Key size={13} color="#3b82f6" />
                    <span>كلمة مرور مؤقتة (8 أحرف على الأقل). سيُطلب من المستخدم تغييرها عند أول تسجيل دخول.</span>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontSize: '12.5px', color: '#94a3b8', fontWeight: 600 }}>
                        تعيين كلمة مرور جديدة (اختياري)
                      </label>
                      <button
                        type="button"
                        onClick={generateSecurePassword}
                        style={{
                          background: 'rgba(59, 130, 246, 0.12)',
                          border: '1px solid rgba(59, 130, 246, 0.35)',
                          color: '#3b82f6',
                          borderRadius: '6px',
                          padding: '3px 8px',
                          fontSize: '11.5px',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        <Sparkles size={12} />
                        إنشاء كلمة مرور
                      </button>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (passwordError) setPasswordError(null);
                        }}
                        dir="ltr"
                        autoComplete="new-password"
                        placeholder="اتركه فارغاً للإبقاء على كلمة المرور الحالية"
                        style={{
                          width: '100%',
                          padding: '9px 38px 9px 12px',
                          borderRadius: '8px',
                          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                          background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                          color: isLight ? '#0f172a' : '#fff',
                          fontSize: '13px',
                          outline: 'none',
                          textAlign: 'left',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#94a3b8',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {password && (
                    <div>
                      <label style={{ display: 'block', fontSize: '12.5px', color: '#94a3b8', marginBottom: '4px', fontWeight: 600 }}>
                        تأكيد كلمة المرور الجديدة *
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          required
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (passwordError) setPasswordError(null);
                          }}
                          dir="ltr"
                          autoComplete="new-password"
                          placeholder="••••••••"
                          style={{
                            width: '100%',
                            padding: '9px 38px 9px 12px',
                            borderRadius: '8px',
                            border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                            background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                            color: isLight ? '#0f172a' : '#fff',
                            fontSize: '13px',
                            outline: 'none',
                            textAlign: 'left',
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{
                            position: 'absolute',
                            left: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Password Validation Error */}
              {passwordError && (
                <div
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#ef4444',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 600,
                  }}
                >
                  <AlertTriangle size={15} />
                  <span>{passwordError}</span>
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', color: '#94a3b8', marginBottom: '4px', fontWeight: 600 }}>
                  الدور المسند *
                </label>
                <select
                  value={userFormData.roleId}
                  onChange={(e) => setUserFormData({ ...userFormData, roleId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                    background: isLight ? '#ffffff' : '#0d1f3c',
                    color: isLight ? '#0f172a' : '#fff',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                >
                  {assignableRoles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.nameAr}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12.5px', color: '#94a3b8', marginBottom: '4px', fontWeight: 600 }}>
                  حالة الحساب
                </label>
                <select
                  value={userFormData.status}
                  onChange={(e) => setUserFormData({ ...userFormData, status: e.target.value as any })}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
                    background: isLight ? '#ffffff' : '#0d1f3c',
                    color: isLight ? '#0f172a' : '#fff',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                >
                  <option value="ACTIVE">نشط (مفعل)</option>
                  <option value="SUSPENDED">مجمد (موقوف)</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  حفظ البيانات
                </button>
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  style={{
                    background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
                    color: isLight ? '#475569' : '#94a3b8',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    fontSize: '13.5px',
                    cursor: 'pointer',
                  }}
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components
const StatCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle: string;
  isLight: boolean;
}> = ({ icon, title, value, subtitle, isLight }) => (
  <div
    style={{
      background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
      border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px',
      padding: '18px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
    }}
  >
    <div
      style={{
        width: '44px',
        height: '44px',
        borderRadius: '10px',
        background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: '20px', fontWeight: 800, color: isLight ? '#0f172a' : '#f8fafc', lineHeight: 1.2 }}>
        {value}
      </div>
      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{subtitle}</div>
    </div>
  </div>
);

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  isLight: boolean;
}> = ({ active, onClick, icon, label, isLight }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: isMobile ? 'center' : 'flex-start',
        gap: '8px',
        padding: isMobile ? '12px 8px' : '10px 16px',
        border: 'none',
        borderBottom: isMobile ? 'none' : (active ? '2.5px solid #3b82f6' : '2.5px solid transparent'),
        borderRadius: isMobile ? '10px' : '0',
        background: isMobile 
          ? (active ? 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(99,102,241,0.15) 100%)' : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)'))
          : 'none',
        color: active ? '#3b82f6' : isLight ? '#64748b' : '#94a3b8',
        fontWeight: active ? 800 : 600,
        fontSize: isMobile ? '11.5px' : '13.5px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        flexShrink: 0,
        whiteSpace: isMobile ? 'normal' : 'nowrap',
        textAlign: 'center',
        borderWidth: isMobile ? '1px' : '0',
        borderStyle: isMobile ? 'solid' : 'none',
        borderColor: isMobile ? (active ? 'rgba(59,130,246,0.3)' : (isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)')) : 'transparent',
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
};



const SimulationCard: React.FC<{
  title: string;
  requiredPermission: string;
  hasPermission: boolean;
  description: string;
  isLight: boolean;
}> = ({ title, requiredPermission, hasPermission, description, isLight }) => (
  <div
    style={{
      padding: '16px',
      borderRadius: '10px',
      background: hasPermission
        ? 'rgba(16,185,129,0.06)'
        : 'rgba(239,68,68,0.06)',
      border: hasPermission
        ? '1px solid rgba(16,185,129,0.25)'
        : '1px solid rgba(239,68,68,0.25)',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontWeight: 700, fontSize: '13.5px', color: isLight ? '#0f172a' : '#f1f5f9' }}>
        {title}
      </span>
      {hasPermission ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#10b981', fontSize: '11.5px', fontWeight: 700 }}>
          <CheckCircle2 size={14} /> مسموح
        </span>
      ) : (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#ef4444', fontSize: '11.5px', fontWeight: 700 }}>
          <Lock size={14} /> محظور
        </span>
      )}
    </div>
    <div style={{ fontSize: '12px', color: '#64748b' }}>{description}</div>
    <div style={{ fontSize: '10.5px', color: '#94a3b8' }}>
      تتطلب الصلاحية: <code>{requiredPermission}</code>
    </div>
  </div>
);

export default UsersPermissionsPage;
